import type { Billing, MemberLink } from './types';

/** Sem acento e em minúsculas: "anúncio" acha "Anuncio". */
export const fold = (text: string): string => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

/** As palavras de uma busca, já sem acento. */
export const searchWords = (text: string): string[] => fold(text).split(/\s+/).filter(Boolean);

/** A cobrança também se busca pelo nome: "anual" acha a assinatura anual sem a palavra no título. */
const BILLING_WORDS: Record<Billing, string> = {
  ONCE: 'pagamento unico',
  WEEKLY: 'assinatura semanal',
  MONTHLY: 'assinatura mensal',
  QUARTERLY: 'assinatura trimestral',
  SEMIANNUAL: 'assinatura semestral',
  YEARLY: 'assinatura anual',
  RECURRING: 'assinatura recorrente',
};

/** O valor procurado, em centavos, de `min` a `max`. */
export interface PriceRange {
  min: number;
  max: number;
  /** Os centavos foram digitados: vale o valor exato, não o real inteiro. */
  exact: boolean;
}

/**
 * O valor digitado: "697", "R$ 2.497", "4.999,97", "29.90". Vazio: nulo; o que
 * não é valor: 'invalid'. Sem os centavos vale o real inteiro — "4999" acha de
 * R$ 4.999,00 a R$ 4.999,99. O ponto é de milhar, a não ser quando fecha o texto
 * com um ou dois dígitos ("29.90").
 */
export function parsePriceQuery(text: string): PriceRange | null | 'invalid' {
  const raw = text.replace(/r\$/gi, '').replace(/\s/g, '');
  if (!raw) return null;
  let integer = raw;
  let fraction: string | null = null;
  if (raw.includes(',')) {
    const parts = raw.split(',');
    if (parts.length > 2) return 'invalid';
    integer = parts[0]!.replace(/\./g, '');
    fraction = parts[1]!;
  } else if (/\.\d{1,2}$/.test(raw)) {
    const at = raw.lastIndexOf('.');
    integer = raw.slice(0, at).replace(/\./g, '');
    fraction = raw.slice(at + 1);
  } else {
    integer = raw.replace(/\./g, '');
  }
  if (!/^\d*$/.test(integer) || (fraction !== null && !/^\d{0,2}$/.test(fraction)) || (!integer && !fraction)) return 'invalid';
  const reais = Number(integer || '0') * 100;
  if (!fraction) return { min: reais, max: reais + 99, exact: false };
  const cents = reais + Number(fraction.padEnd(2, '0'));
  return { min: cents, max: cents, exact: true };
}

/** O link entra no resultado: todas as palavras no produto, na oferta, na plataforma ou na cobrança, e o preço na faixa. */
export function matchesLink(link: MemberLink, words: readonly string[], price: PriceRange | null): boolean {
  if (price && (link.price === null || link.price < price.min || link.price > price.max)) return false;
  if (words.length === 0) return true;
  const text = fold([link.productName, link.offerTitle ?? '', link.group, link.billing ? BILLING_WORDS[link.billing] : ''].join(' '));
  return words.every((word) => text.includes(word));
}
